import type { ReactNode } from "react";
import { InputNumber } from "antd";
import { useTranslation } from "react-i18next";
import {
  BillTotalsBreakdown,
  type BillClientDiscount,
} from "./BillTotalsBreakdown";
import { BANKNOTES } from "./CashChangePanel";

interface MixedPaymentPanelProps {
  /** Общо за плащане (низ от bill.total_amount). */
  totalAmount: string;
  cashAmount: number;
  cardAmount: number;
  /** Родителят допълва другата половина до тотала (както досега). */
  onCashChange: (cash: number) => void;
  onCardChange: (card: number) => void;
  disabled?: boolean;
  /** Блокът „Общо" в дъното — на телефон (старият 1:1 изглед) го няма. */
  showTotal?: boolean;
  /** Широкият изглед по новия дизайн; телефонът остава със стария компактен. */
  large?: boolean;
  /** Клиентският процент — редовете над „Общо" (виж BillTotalsBreakdown). */
  discount?: BillClientDiscount | null;
  /** Режимът с бакшиш: картата „Бакшиш" над „Общо" и редът под „Общо"
   *  („Бакшиш X EUR отделно · метод"); без тях панелът е старият. */
  tipSlot?: ReactNode;
  totalNote?: ReactNode;
}

/**
 * Разпределение на „Смесено" плащане — лявата колона на модала за закриване:
 * „В брой" (с бързите банкноти) и „С карта" едно под друго, „Остава за
 * доплащане" под тях, „Общо" в дъното. Функционално 1:1 със стария блок
 * (parser запетая→точка, до тотала, фокус селектира, родителят допълва
 * другата половина) — сменени са само подредбата и обличането.
 */
export const MixedPaymentPanel = ({
  totalAmount,
  cashAmount,
  cardAmount,
  onCashChange,
  onCardChange,
  disabled,
  showTotal = true,
  large = false,
  discount = null,
  tipSlot = null,
  totalNote,
}: MixedPaymentPanelProps) => {
  const { t } = useTranslation();
  const total = parseFloat(totalAmount) || 0;
  const entered = cashAmount + cardAmount;
  const remaining = Math.max(0, total - entered);

  // Еднаква височина в двете части на InputNumber — antd вътрешният input е
  // недостижим без ant-* селектора (разрешено изключение 2)
  const inputClassName = large
    ? "!w-full !h-12 !rounded-xl !border-pos-ctl !text-base [&_.ant-input-number-input]:!h-[46px] [&_.ant-input-number-input]:!text-right [&_.ant-input-number-input]:font-pos-mono [&_.ant-input-number-input]:!font-bold"
    : "!w-full !h-10 !rounded-[10px] !border-pos-line2 [&_.ant-input-number-input]:!h-[38px]";

  const numberInputProps = {
    min: 0,
    max: total,
    precision: 2,
    step: 0.01,
    controls: false,
    disabled,
    className: inputClassName,
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.target.select(),
    parser: (value?: string) => {
      const parsed = value?.replace(/,/g, ".") || "0";
      return parseFloat(parsed) || 0;
    },
  };

  const microLabelClassName =
    "mb-1.5 text-[10.5px] font-bold uppercase tracking-[1.2px] text-pos-muted";

  if (!large) {
    // Телефон: старият компактен блок 1:1 — етикети + две полета + сборът
    return (
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="rounded-2xl border border-pos-line2 bg-pos-canvas p-3.5">
          <div className={microLabelClassName}>
            {t("closeTable.paymentDistribution")}
          </div>

          <div className="flex flex-col gap-2.5">
            <div>
              <div className="mb-1 text-[11px] text-pos-muted">
                {t("closeTable.cash")}
              </div>
              <InputNumber
                {...numberInputProps}
                value={cashAmount}
                onChange={(value) => onCashChange(value || 0)}
              />
            </div>
            <div>
              <div className="mb-1 text-[11px] text-pos-muted">
                {t("closeTable.card")}
              </div>
              <InputNumber
                {...numberInputProps}
                value={cardAmount}
                onChange={(value) => onCardChange(value || 0)}
              />
            </div>
          </div>

          <div className="mt-2.5 font-pos-mono text-[11px] text-pos-muted">
            {t("closeTable.mixedTotal", {
              entered: entered.toFixed(2),
              total: total.toFixed(2),
            })}
          </div>
        </div>

        {showTotal && (
          <BillTotalsBreakdown
            total={total}
            discount={discount}
            dividerClassName="bg-pos-line2"
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      {/* Елементите лежат направо върху тонираната колона (бели карти върху
          топлия фон). В брой — банкнотите се ДОБАВЯТ към сумата в брой,
          картата се допълва автоматично от родителя */}
      <div className={microLabelClassName}>{t("closeTable.cash")}</div>
      <div className="mb-2 grid grid-cols-5 gap-1.5">
        {BANKNOTES.map((banknote) => (
          <button
            key={banknote}
            type="button"
            disabled={disabled}
            onClick={() => onCashChange(Math.min(total, cashAmount + banknote))}
            className="h-10 cursor-pointer rounded-[10px] border border-pos-ctl bg-pos-card font-pos-mono text-sm font-semibold text-pos-ink2 transition-colors hover:border-pos-accent/40 disabled:cursor-not-allowed disabled:opacity-50"
          >
            +{banknote}
          </button>
        ))}
      </div>
      <InputNumber
        {...numberInputProps}
        value={cashAmount}
        onChange={(value) => onCashChange(value || 0)}
      />

      {/* С карта */}
      <div className={`${microLabelClassName} mt-4`}>
        {t("closeTable.card")}
      </div>
      <InputNumber
        {...numberInputProps}
        value={cardAmount}
        onChange={(value) => onCardChange(value || 0)}
      />

      {/* Остава за доплащане — подсказката за пълната сума е във футъра */}
      <div className="mt-4 flex items-baseline justify-between gap-2 px-0.5">
        <span className="text-[11px] font-bold uppercase tracking-[1.2px] text-pos-sec-head">
          {t("closeTable.remaining")}
        </span>
        <span className="font-pos-mono text-[21px] font-bold tracking-[-0.5px] text-pos-accent">
          {remaining.toFixed(2)} <span className="text-sm">EUR</span>
        </span>
      </div>

      {tipSlot && <div className="mt-4">{tipSlot}</div>}

      {showTotal && (
        <BillTotalsBreakdown total={total} discount={discount} note={totalNote} />
      )}
    </div>
  );
};
